import { useMemo, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Button, IconButton, Stack, Alert, Tooltip, Typography, Box,
  TextField, MenuItem, Switch, FormControlLabel,
} from '@mui/material';
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Add, Edit, Delete } from '@mui/icons-material';
import PageShell from '../../components/layout/PageShell';
import PageHeader from '../../components/layout/PageHeader';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { withSerialNo } from '../../components/ui/gridColumns';
import TenantSubscriptionPlanFormDrawer from '../../components/tenants/TenantSubscriptionPlanFormDrawer';
import {
  listTenantSubscriptionPlans,
  createTenantSubscriptionPlan,
  updateTenantSubscriptionPlan,
  setTenantSubscriptionPlanStatus,
  deleteTenantSubscriptionPlan,
  extractApiError,
  formatPlanBillingLabel,
  formatPlanPrice,
  type TenantSubscriptionPlanPayload,
  type TenantSubscriptionPlanRow,
} from '../../services/tenantSubscriptionPlanService';
import { formatMoney } from '../../utils/currency';

type StatusFilter = 'all' | 'active' | 'inactive';

export default function TenantSubscriptionPlansPage() {
  const queryClient = useQueryClient();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [mode, setMode] = useState<'create' | 'edit'>('create');
  const [selected, setSelected] = useState<TenantSubscriptionPlanRow | null>(null);
  const [formError, setFormError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<TenantSubscriptionPlanRow | null>(null);
  const [mutationError, setMutationError] = useState('');
  const [statusFilter, setStatusFilter] = useState<StatusFilter>('all');

  const { data: plans = [], isLoading } = useQuery({
    queryKey: ['tenant-subscription-plans'],
    queryFn: () => listTenantSubscriptionPlans(),
  });

  const filteredPlans = useMemo(() => {
    if (statusFilter === 'active') return plans.filter((p) => p.isActive);
    if (statusFilter === 'inactive') return plans.filter((p) => !p.isActive);
    return plans;
  }, [plans, statusFilter]);

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['tenant-subscription-plans'] });
    queryClient.invalidateQueries({ queryKey: ['tenant-subscription-plans', 'public'] });
  };

  const createMut = useMutation({
    mutationFn: createTenantSubscriptionPlan,
    onSuccess: () => { invalidate(); setDrawerOpen(false); setFormError(''); },
    onError: (err) => setFormError(extractApiError(err)),
  });

  const updateMut = useMutation({
    mutationFn: ({ id, payload }: { id: string; payload: Partial<TenantSubscriptionPlanPayload> }) =>
      updateTenantSubscriptionPlan(id, payload),
    onSuccess: () => { invalidate(); setDrawerOpen(false); setFormError(''); },
    onError: (err) => setFormError(extractApiError(err)),
  });

  const statusMut = useMutation({
    mutationFn: ({ id, isActive }: { id: string; isActive: boolean }) =>
      setTenantSubscriptionPlanStatus(id, isActive),
    onSuccess: () => { setMutationError(''); invalidate(); },
    onError: (err) => setMutationError(extractApiError(err)),
  });

  const deleteMut = useMutation({
    mutationFn: deleteTenantSubscriptionPlan,
    onSuccess: () => {
      setMutationError('');
      invalidate();
      setDeleteTarget(null);
    },
    onError: (err) => {
      setMutationError(extractApiError(err));
      setDeleteTarget(null);
    },
  });

  const openCreate = () => {
    setMode('create');
    setSelected(null);
    setFormError('');
    setDrawerOpen(true);
  };

  const openEdit = (plan: TenantSubscriptionPlanRow) => {
    setMode('edit');
    setSelected(plan);
    setFormError('');
    setDrawerOpen(true);
  };

  const handleSubmit = (payload: TenantSubscriptionPlanPayload) => {
    if (mode === 'create') createMut.mutate(payload);
    else if (selected) updateMut.mutate({ id: selected._id, payload });
  };

  const columns: GridColDef[] = useMemo(() => [
    { field: 'code', headerName: 'Code', width: 100 },
    { field: 'name', headerName: 'Plan', flex: 1, minWidth: 130 },
    {
      field: 'price',
      headerName: 'Price',
      width: 120,
      minWidth: 100,
      valueGetter: (_v, row) => (
        row.price
          ? formatMoney(row.price, row.currency || 'INR')
          : 'Custom'
      ),
    },
    {
      field: 'billingInterval',
      headerName: 'Billing cycle',
      width: 140,
      minWidth: 130,
      valueGetter: (_v, row) => formatPlanBillingLabel(row.billingInterval),
      renderCell: ({ row }) => (
        <Typography variant="body2" title={formatPlanPrice(row)}>
          {formatPlanBillingLabel(row.billingInterval)}
        </Typography>
      ),
    },
    {
      field: 'trialDays',
      headerName: 'Trial',
      width: 80,
      valueGetter: (_v, row) => (row.trialDays > 0 ? `${row.trialDays} days` : '—'),
    },
    {
      field: 'maxUsers', headerName: 'Users', width: 70,
      valueGetter: (_v, row) => row.maxUsers ?? '∞',
    },
    {
      field: 'isActive',
      headerName: 'Status',
      width: 150,
      sortable: false,
      renderCell: (params) => {
        const plan = params.row as TenantSubscriptionPlanRow;
        const busy = statusMut.isPending && statusMut.variables?.id === plan._id;
        return (
          <FormControlLabel
            sx={{ m: 0, gap: 0.5 }}
            control={(
              <Switch
                size="small"
                color="success"
                checked={plan.isActive}
                disabled={busy}
                onChange={(e) => {
                  e.stopPropagation();
                  statusMut.mutate({ id: plan._id, isActive: e.target.checked });
                }}
              />
            )}
            label={(
              <Typography variant="caption" fontWeight={700} color={plan.isActive ? 'success.main' : 'text.secondary'}>
                {plan.isActive ? 'Active' : 'Inactive'}
              </Typography>
            )}
          />
        );
      },
    },
    {
      field: 'actions', headerName: 'Actions', width: 100, sortable: false,
      renderCell: (params) => {
        const plan = params.row as TenantSubscriptionPlanRow;
        return (
          <Stack direction="row" spacing={0.25}>
            <Tooltip title="Edit">
              <IconButton size="small" onClick={() => openEdit(plan)}><Edit fontSize="small" /></IconButton>
            </Tooltip>
            <Tooltip title="Delete plan">
              <IconButton
                size="small"
                color="error"
                onClick={() => {
                  setMutationError('');
                  setDeleteTarget(plan);
                }}
              >
                <Delete fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        );
      },
    },
  ], [statusMut]);

  return (
    <PageShell>
      <PageHeader
        gradient
        eyebrow="Tenant Master"
        title="Subscription plans"
        subtitle="SaaS tiers for tenants — pricing, limits, and plan highlights."
        actions={<Button variant="contained" color="inherit" startIcon={<Add />} onClick={openCreate}>Add plan</Button>}
      />

      {mutationError && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setMutationError('')}>
          {mutationError}
        </Alert>
      )}

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} alignItems={{ sm: 'center' }} sx={{ mb: 2 }}>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value as StatusFilter)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">All statuses</MenuItem>
          <MenuItem value="active">Active</MenuItem>
          <MenuItem value="inactive">Inactive</MenuItem>
        </TextField>
        <Typography variant="body2" color="text.secondary">
          Toggle status in the list. Plans assigned to tenants cannot be deleted — deactivate them instead.
        </Typography>
      </Stack>

      <DataGrid
        rows={filteredPlans}
        columns={withSerialNo(columns)}
        loading={isLoading}
        getRowId={(row) => row._id}
        autoHeight
        disableRowSelectionOnClick
        pageSizeOptions={[10, 25]}
        initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
        sx={{ bgcolor: 'background.paper', borderRadius: 2 }}
        localeText={{
          noRowsLabel: statusFilter === 'all' ? 'No subscription plans' : `No ${statusFilter} plans`,
        }}
      />

      <TenantSubscriptionPlanFormDrawer
        open={drawerOpen}
        mode={mode}
        plan={selected}
        existingCodes={plans.map((p) => p.code)}
        loading={createMut.isPending || updateMut.isPending}
        error={formError}
        onClose={() => { setDrawerOpen(false); setFormError(''); }}
        onSubmit={handleSubmit}
      />

      <ConfirmDialog
        open={!!deleteTarget}
        title="Delete subscription plan?"
        message={
          deleteTarget
            ? `Are you sure you want to delete "${deleteTarget.name}" (${deleteTarget.code})? `
              + 'If any tenant is using this plan, deletion will be blocked — deactivate the plan instead.'
            : ''
        }
        confirmLabel="Delete"
        confirmColor="error"
        loading={deleteMut.isPending}
        onConfirm={() => deleteTarget && deleteMut.mutate(deleteTarget._id)}
        onCancel={() => setDeleteTarget(null)}
      />
    </PageShell>
  );
}
